import React, { useState, useEffect } from 'react';
import { txOptions, labelToColor, ColorsDictionary } from './txOptions';
import { StateType } from './types';
import { useCareFlow360Store } from '../hooks/useCareFlow360Store';
import { convertNotation } from './notationUtils';

interface ApexoToothStateWheelProps {
  toothId: number;
  initialValue?: string;
  type: StateType;
  onSet: (_label: string | null) => void;
  onClose: () => void;
}

export default function ApexoToothStateWheel({
  toothId,
  initialValue,
  type,
  onSet,
  onClose,
}: ApexoToothStateWheelProps) {
  const [selectedLabel, setSelectedLabel] = useState<string | null>(initialValue || null);
  const [isOtherMode, setIsOtherMode] = useState(false);
  const [otherText, setOtherText] = useState('');

  const treatments = txOptions.filter((x) => x.type !== 'state');
  const states = txOptions.filter((x) => x.type !== 'treatment');

  const options = type === 'both' ? txOptions : (type === 'state' ? states : treatments);

  const notation = useCareFlow360Store(s => s.notation);

  useEffect(() => {
    if (initialValue && !txOptions.find((x) => x.label === initialValue || x.masterLabel === initialValue)) {
      setIsOtherMode(true);
      setOtherText(initialValue);
    }
  }, [initialValue]);

  const handleSelect = (label: string | null) => {
    setSelectedLabel(label);
    setIsOtherMode(false);
    onSet(label);
  };

  const handleOtherSave = () => {
    handleSelect(otherText);
  };

  const centerColor = selectedLabel ? labelToColor(selectedLabel) : ColorsDictionary.c18;

  const renderRadialButtons = () => {
    return options.map((opt, index) => {
      const angle = (index * 2 * Math.PI) / options.length - Math.PI / 2;
      const radius = 190;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;

      const isLast = index === options.length - 1;
      const isSelected = selectedLabel === opt.label || selectedLabel === opt.masterLabel || (isLast && isOtherMode);

      return (
        <button
          key={opt.label}
          onClick={() => {
            if (isLast) {
              setSelectedLabel("");
              setIsOtherMode(true);
            } else {
              handleSelect(opt.masterLabel || opt.label);
            }
          }}
          className={`radial-option ${isSelected ? 'active' : ''}`}
          style={{
            transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
            '--tx-x': `${x}px`,
            '--tx-y': `${y}px`,
            backgroundColor: (isOtherMode && !isLast) ? 'transparent' : opt.color,
            boxShadow: isSelected ? `0 0 20px ${opt.color}` : `0 4px 12px rgba(0,0,0,0.1)`,
            color: 'white',
            zIndex: isSelected ? 100 : 1,
          } as React.CSSProperties}
        >
          <span className="radial-icon">{opt.icon}</span>
          <span className="radial-label">{opt.label}</span>
        </button>
      );
    });
  };

  return (
    <div className="apexo-wheel-backdrop" onClick={onClose}>
      <div className="apexo-wheel-container" onClick={(e) => e.stopPropagation()}>
        <div className="apexo-wheel">
          {renderRadialButtons()}
          
          <div className="apexo-wheel-center">
            <div className="tooth-display" style={{ color: centerColor }}>
              <span className="tooth-iso">{convertNotation(toothId, notation)}</span>
            </div>

            {isOtherMode ? (
              <input 
                autoFocus
                className="other-input" 
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleOtherSave()}
              />
            ) : selectedLabel ? (
              <div 
                className="selected-label-badge" 
                style={{ backgroundColor: centerColor }}
              >
                {txOptions.find(x => x.label === selectedLabel || x.masterLabel === selectedLabel)?.label || selectedLabel}
              </div>
            ) : (
              <div className="instruction-text">
                {type === 'state'
                  ? "Tap condition or history to register"
                  : "Tap treatment performed to register"}
              </div>
            )}

            <div className="action-buttons">
              {selectedLabel && (
                <button className="btn-delete" onClick={() => handleSelect(null)}>
                  🗑 Delete
                </button>
              )}
              {isOtherMode && (
                <button className="btn-save" onClick={handleOtherSave}>
                  💾 Save
                </button>
              )}
              {!isOtherMode && !selectedLabel && (
                <button className="btn-close" onClick={onClose}>
                  ✖ Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
